import Endpoints from 'constants/Endpoints';
import {RestRequestBLOB, RestRequestDELETE, RestRequestGET, RestRequestPOST, RestRequestPUT} from 'libs/RestRequest';
import reduxStore from 'redux/store';
import {clearResource, setResource, setSaveStatusError} from 'redux/actions';
import applySearchParams from "../libs/applySearchParams";
import PrivilegeService from "./PrivilegeService";
import downloadFileWithPreloadUrl from "../libs/downloadFileWithPreloadUrl";
import Tr from "translations/Translations";
import isResponseValid from "../libs/isResponseValid";

const CORRESPONDENCE_RES_PREFIX = 'correspondence';
const CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID = CORRESPONDENCE_RES_PREFIX + "BeforeBiddingEndThreads";
const CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID = CORRESPONDENCE_RES_PREFIX + "AfterBiddingEndThreads";
const CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID = CORRESPONDENCE_RES_PREFIX + "NotSplicedThreads";
const CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID = CORRESPONDENCE_RES_PREFIX + "LastCheckTime";
const CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID = CORRESPONDENCE_RES_PREFIX + "SplitDate";
const CORRESPONDENCE_DOWNLOAD_ATTACHMENT_REGISTRATION_DATE_RESOURCE_ID = CORRESPONDENCE_RES_PREFIX + "DownloadAttachmentRegistrationDate";

const cleanupPrivileges = () => {
    PrivilegeService.cleanupPrivileges(PrivilegeService.CORRESPONDENCE_PRIVILEGE_STORE_KEY);
}

const cleanupThreadList = () => {
    reduxStore.dispatch(clearResource(CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID));
    reduxStore.dispatch(clearResource(CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID));
    reduxStore.dispatch(clearResource(CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID));
    reduxStore.dispatch(clearResource(CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID));
    reduxStore.dispatch(clearResource(CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID));
    reduxStore.dispatch(clearResource(CORRESPONDENCE_DOWNLOAD_ATTACHMENT_REGISTRATION_DATE_RESOURCE_ID));
};

const getContractorThreadList = async (roundId) => {
    const threadList = await RestRequestGET(Endpoints.CORRESPONDENCE_CONTRACTOR_THREAD_LIST(roundId));

    reduxStore.dispatch(setResource(CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID, threadList.checkTime));
    reduxStore.dispatch(setResource(CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID, threadList.correspondenceSplitDate));
    reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID, threadList.beforeBiddingEndThreads));
    reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID, threadList.afterBiddingEndThreads));
    reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID, threadList.notSplicedThreads));
    reduxStore.dispatch(setResource(CORRESPONDENCE_DOWNLOAD_ATTACHMENT_REGISTRATION_DATE_RESOURCE_ID, threadList.attachmentDownloadRegistrationStartDate))
}

const getBuyerNewMessages = async (roundId) => {
    const state = reduxStore.getState();
    let params = applySearchParams({dateFrom: state.resources[CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID]})

    const response = await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_NEW_MESSAGES(roundId), {params});
    if (response.success !== false) {
        const unreadIds = _processNewMessagesUpdate(response);
        if (unreadIds.length > 0) {
            RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_MARK_AS_READ(roundId), {list: unreadIds});
        }
    }
}

const _processNewMessagesUpdate = (response) => {
    reduxStore.dispatch(setResource(CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID, response.checkTime));
    reduxStore.dispatch(setResource(CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID, response.correspondenceSplitDate));
    const state = reduxStore.getState();
    const newReadMessageIds = [];
    for (const listResourceId of [CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID, CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID, CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID]) {
        const reqResource = listResourceId.substring(CORRESPONDENCE_RES_PREFIX.length).charAt(0).toLowerCase() + listResourceId.substring(CORRESPONDENCE_RES_PREFIX.length).slice(1);
        if (response?.[reqResource]?.threads?.length) {
            const threadsObject = response[reqResource].threads.reduce(function (o, val) {
                o[val.threadId] = val;
                return o;
            }, {});
            if (state?.resources?.[listResourceId]?.threads) {
                const threads = state.resources[listResourceId].threads.map(th => {
                    if (threadsObject.hasOwnProperty(th.threadId)) {
                        const reqThread = threadsObject[th.threadId];
                        const {messages, unreadMessagesCount, ...others} = th;
                        let newMessageList = null;
                        if (messages) {
                            let index = messages.findIndex(x => x.status === 'saved');
                            if (index === -1) index = messages.length;
                            newMessageList = [...messages];
                            newMessageList.splice(index, 0, ...reqThread.messages);
                            newReadMessageIds.push(...reqThread.messages.map(x => x.messageId));
                        }
                        delete threadsObject[th.threadId];
                        return ({
                            ...others,
                            unreadMessagesCount: unreadMessagesCount + reqThread.messages.length,
                            messages: newMessageList
                        })
                    }
                    return th;
                });
                Object.values(threadsObject).forEach(x => threads.unshift({...x, messages: null}));

                reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
            }

        }
    }
    return newReadMessageIds;
}

const getContractorNewMessages = async (roundId) => {
    const state = reduxStore.getState();
    let params = applySearchParams({dateFrom: state.resources[CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID]})

    const response = await RestRequestGET(Endpoints.CORRESPONDENCE_CONTRACTOR_NEW_MESSAGES(roundId), {params});
    if (response.success !== false) {
        const unreadIds = _processNewMessagesUpdate(response);
        if (unreadIds.length > 0) {
            RestRequestPOST(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_MARK_AS_READ(roundId), {list: unreadIds});
        }
    }
}

const getBuyerThreadList = async (roundId) => {
    const threadList = await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_THREAD_LIST(roundId));
    reduxStore.dispatch(setResource(CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID, threadList.checkTime));
    reduxStore.dispatch(setResource(CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID, threadList.correspondenceSplitDate));
    reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID, threadList.beforeBiddingEndThreads));
    reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID, threadList.afterBiddingEndThreads));
    reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID, threadList.notSplicedThreads));
    reduxStore.dispatch(setResource(CORRESPONDENCE_DOWNLOAD_ATTACHMENT_REGISTRATION_DATE_RESOURCE_ID, threadList.attachmentDownloadRegistrationStartDate));
};

const getBuyerContractorInRoundList = async (roundId) => {
    return await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_CTR_IN_ROUND(roundId));
};

const getBuyerThreadMessageList = async (roundId, threadId, listResourceId) => {
    const messages = await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_LIST(roundId, threadId));
    if (messages.success !== false) {
        const unreadIds = messages.filter(x => !x.read && (x.contractorUser || x.publicQuestion)).map(x => x.messageId);
        if (unreadIds.length > 0) {
            RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_MARK_AS_READ(roundId), {list: unreadIds});
        }
        _setMessagesInThread(threadId, messages, listResourceId);
    }
}

const getBuyerMessageDelivery = async (roundId, messageId, type, identity) => {
    return await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_EMAIL_DELIVERY(roundId, messageId, type, identity));
}

const getContractorThreadMessageList = async (roundId, threadId, listResourceId) => {
    const messages = await RestRequestGET(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_LIST(roundId, threadId));
    if (messages.success !== false) {
        const unreadIds = messages.filter(x => !x.read && x.buyerUser).map(x => x.messageId);
        if (unreadIds.length > 0) {
            RestRequestPOST(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_MARK_AS_READ(roundId), {list: unreadIds});
        }
        _setMessagesInThread(threadId, messages, listResourceId);
    }
}

const _setMessagesInThread = (threadId, reqMessages, listResourceId) => {
    const state = reduxStore.getState();
    if (state?.resources?.[listResourceId]?.threads) {
        const threads = state.resources[listResourceId].threads.map(th => {
            if (th.threadId === threadId) {
                const {messages, ...others} = th;
                return ({
                    ...others,
                    messages: reqMessages
                })
            }
            return th;
        })
        reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
    }
}

const _addThreadToList = (thread, listResourceId) => {
    if (thread.threadId) {
        const state = reduxStore.getState();
        if (state?.resources?.[listResourceId]?.threads) {
            const threads = [
                thread,
                ...state.resources[listResourceId].threads
            ]
            reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
        }
    }
}

const addBuyerThread = async (roundId, thread, listResourceId) => {
    const response = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_THREAD_ADD(roundId), thread);
    if (response.success !== false) {
        _addThreadToList(response, listResourceId);
    }
    return response;
}

const addContractorThread = async (roundId, thread, listResourceId) => {
    const response = await RestRequestPOST(Endpoints.CORRESPONDENCE_CONTRACTOR_THREAD_ADD(roundId), thread);
    if (response.success !== false) {
        _addThreadToList(response, listResourceId);
    }
    return response;
}
const saveBuyerThreadAsContractor = async (roundId, thread, listResourceId) => {
    const response = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_THREAD_AS_CRT_SEND(roundId), thread);
    if (response.success !== false) {
        _addThreadToList(response, listResourceId);
    }
    return response;
}

const _updateMessageInThread = (threadId, messageId, listResourceId, response) => {
    const state = reduxStore.getState();
    if (state?.resources?.[listResourceId]?.threads) {
        const threads = state.resources[listResourceId].threads.map(th => {
            if (th.threadId === threadId) {
                const {messages, ...other} = th;
                if (messages) {
                    return ({
                        ...other,
                        messages: messages.map(msg => (msg.messageId === messageId ? response : msg))
                    })
                }
            }
            return th;
        });
        reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
    }
};

const clearThreadMessages = (threadId, listResourceId) => {
    const state = reduxStore.getState();
    if (state?.resources?.[listResourceId]?.threads) {
        const threads = state.resources[listResourceId].threads.map(th => {

            if (th.threadId === threadId) {
                const {messages, unreadMessagesCount, ...other} = th;
                if (messages) {
                    return ({
                        ...other,
                        unreadMessagesCount: 0,
                        messages: null
                    })
                }
            }
            return th;

        });

        reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
    }
};

const updateBuyerMessage = async (roundId, threadId, messageId, listResourceId, message) => {
    const resp = await RestRequestPUT(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_UPDATE(roundId, messageId), message);
    if (resp.success !== false) {
        _updateMessageInThread(threadId, messageId, listResourceId, resp);
    }
    return resp;
}

const updateContractorMessage = async (roundId, threadId, messageId, listResourceId, message) => {
    const resp = await RestRequestPUT(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_UPDATE(roundId, messageId), message);
    if (resp.success !== false) {
        _updateMessageInThread(threadId, messageId, listResourceId, resp);
    }
    return resp;
}

const sendBuyerMessage = async (roundId, threadId, messageId, listResourceId) => {
    const resp = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_SEND(roundId, messageId));
    if (resp.success !== false) {
        _updateMessageInThread(threadId, messageId, listResourceId, resp);
    }
    return resp;
}

const sendContractorMessage = async (roundId, threadId, messageId, listResourceId) => {
    const resp = await RestRequestPOST(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_SEND(roundId, messageId));
    if (resp.success !== false) {
        _updateMessageInThread(threadId, messageId, listResourceId, resp);
    }
    return resp;
}

const publishBuyerMessage = async (roundId, threadId, messageId, listResourceId) => {
    const resp = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_PUBLISH(roundId, messageId));
    if (resp.success !== false) {
        _updateMessagesInThreads(listResourceId, resp);
    }
    return resp;
}

const deleteBuyerMessage = async (roundId, threadId, messageId, listResourceId) => {
    const resp = await RestRequestDELETE(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_DELETE(roundId, messageId));
    if (resp.success) {
        _removeMessagesFromThread(threadId, messageId, listResourceId);
    }
    return resp;
}

const deleteContractorMessage = async (roundId, threadId, messageId, listResourceId) => {
    const resp = await RestRequestDELETE(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_DELETE(roundId, messageId));
    if (resp.success) {
        _removeMessagesFromThread(threadId, messageId, listResourceId);
    }
    return resp;
}

const _removeMessagesFromThread = (threadId, messageId, listResourceId) => {
    const state = reduxStore.getState();
    if (state?.resources?.[listResourceId]?.threads) {
        let removedLast = false;
        let threads = state.resources[listResourceId].threads.map(th => {
            if (th.threadId === threadId) {

                const {messages, ...other} = th;
                const messagesFiltered = messages.filter(msg => (msg.messageId !== messageId));
                if (messagesFiltered.length === 0) {
                    removedLast = true;
                }
                return ({
                    ...other,
                    messages: messagesFiltered
                })
            }
            return th;
        });
        if (removedLast) {
            threads = threads.filter(t => t.threadId !== threadId);
        }

        reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
    }
};

const _updateMessagesInThreads = (listResourceId, response) => {
    const state = reduxStore.getState();
    if (state?.resources?.[listResourceId]?.threads) {
        const threads = state.resources[listResourceId].threads.map(th => {
            const respTh = response.find(x => x.threadId === th.threadId);
            if (respTh) {
                const mesMap = respTh.messages.reduce(function (acc, cur) {
                    acc[cur.messageId] = cur;
                    return acc;
                }, {});

                const {messages, ...other} = th;
                if (messages) {
                    return ({
                        ...other,
                        messages: messages.map(msg => (mesMap.hasOwnProperty(msg.messageId) ? mesMap[msg.messageId] : msg))
                    })
                }
            }
            return th;
        });
        reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
    }
};


const _addMessagesToThreads = (listResourceId, threadList) => {
    const state = reduxStore.getState();
    if (state?.resources?.[listResourceId]?.threads) {
        const threads = state.resources[listResourceId].threads.map(th => {
            const respTh = threadList.find(x => x.threadId === th.threadId);
            if (respTh) {
                const {messages, ...other} = th;
                if (messages) {
                    return ({
                        ...other,
                        messages: [...messages, ...respTh.messages]
                    })
                }
            }
            return th;
        });
        reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
    }
};
const sendBuyerBulkMessages = async (roundId, bulkMessages, listResourceId) => {
    const resp = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_BULK_MESSAGES_SEND(roundId), bulkMessages);
    if (resp.success !== false) {
        _addMessagesToThreads(listResourceId, resp);
    }
    return resp;
}

const publishBuyerBulkMessages = async (roundId, bulkMessages, listResourceId) => {
    const resp = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_BULK_MESSAGES_PUBLISH(roundId), bulkMessages);
    if (resp.success !== false) {
        _addMessagesToThreads(listResourceId, resp);
    }
    return resp;
}

const createBuyerMessage = async (roundId, threadId, listResourceId) => {
    const message = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_CREATE(roundId, threadId));
    _addMessagesToThreads(listResourceId, [{threadId, messages: [message]}]);
    return message;
}

const createContractorMessage = async (roundId, threadId, listResourceId) => {
    const message = await RestRequestPOST(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_CREATE(roundId, threadId));
    _addMessagesToThreads(listResourceId, [{threadId, messages: [message]}]);
    return message;
}

const _modifyFlag = (threadId, listResourceId, flag) => {
    const state = reduxStore.getState();
    if (state?.resources?.[listResourceId]?.threads) {
        const threads = state.resources[listResourceId].threads.map(x => (x.threadId !== threadId ? x : {...x, hasRedFlag: flag}));
        reduxStore.dispatch(setResource(listResourceId, {...state.resources[listResourceId], threads}));
    }
}

const getContractorPrivileges = async (roundId) => {
    await PrivilegeService.getPrivileges(Endpoints.CORRESPONDENCE_CONTRACTOR_PRIVILEGES(roundId), PrivilegeService.CORRESPONDENCE_PRIVILEGE_STORE_KEY);
}

const getBuyerPrivileges = async (roundId) => {
    await PrivilegeService.getPrivileges(Endpoints.CORRESPONDENCE_BUYER_PRIVILEGES(roundId), PrivilegeService.CORRESPONDENCE_PRIVILEGE_STORE_KEY);
}

const sendBuyerFlagThread = async (roundId, threadId, listResourceId) => {
    return await _sendFlagUnflagThread(Endpoints.CORRESPONDENCE_BUYER_FLAG, roundId, threadId, listResourceId, true);
}

const sendBuyerUnflagThread = async (roundId, threadId, listResourceId) => {
    return await _sendFlagUnflagThread(Endpoints.CORRESPONDENCE_BUYER_UNFLAG, roundId, threadId, listResourceId, false);
}

const sendContractorFlagThread = async (roundId, threadId, listResourceId) => {
    return await _sendFlagUnflagThread(Endpoints.CORRESPONDENCE_CONTRACTOR_FLAG, roundId, threadId, listResourceId, true);
}

const sendContractorUnflagThread = async (roundId, threadId, listResourceId) => {
    return await _sendFlagUnflagThread(Endpoints.CORRESPONDENCE_CONTRACTOR_UNFLAG, roundId, threadId, listResourceId, false);
}

const _sendFlagUnflagThread = async (urlFn, roundId, threadId, listResourceId, flag) => {
    const resp = await RestRequestPOST(urlFn(roundId, threadId));
    if (resp.result) {
        _modifyFlag(threadId, listResourceId, flag);
    }
}

const downloadBuyerMessageAttachmentsZip = async (roundId, messageId, finallyCallback = () => {
}) => {
    try {
        RestRequestBLOB(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_ATTACHMENTS_ZIP(roundId, messageId))
            .finally(finallyCallback);
    } catch (error) {
    }
}

const downloadBuyerMessageAttachment = async (roundId, attachmentId, finallyCallback = () => {
}) => {
    await downloadFileWithPreloadUrl(Endpoints.CORRESPONDENCE_BUYER_ATTACHMENT_URL(roundId, attachmentId), false)
        .finally(finallyCallback);
}

const checkVerificationLimit = async (roundId, messageId) => {
    return await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_ATTACHMENTS_CHECK_LIMIT(roundId, messageId));
}
const printBuyerMessageAttachmentsVerification = async (roundId, messageId, finallyCallback = () => {
}) => {
    RestRequestBLOB(Endpoints.CORRESPONDENCE_BUYER_MESSAGE_ATTACHMENTS_VERIFY(roundId, messageId))
        .finally(finallyCallback);
}

const downloadContractorMessageAttachmentsZip = async (roundId, messageId, finallyCallback = () => {
}) => {
    RestRequestBLOB(Endpoints.CORRESPONDENCE_CONTRACTOR_MESSAGE_ATTACHMENTS_ZIP(roundId, messageId))
        .finally(finallyCallback);
}

const downloadContractorMessageAttachment = async (roundId, attachmentId, finallyCallback = () => {
}) => {
    await downloadFileWithPreloadUrl(Endpoints.CORRESPONDENCE_CONTRACTOR_ATTACHMENT_URL(roundId, attachmentId), false)
        .finally(finallyCallback);
}

const getContractorSearchResult = async (roundId, searchObject) => {
    return await getSearchResult(searchObject, Endpoints.CORRESPONDENCE_CONTRACTOR_SEARCH(roundId));
}

const getBuyerSearchResult = async (roundId, searchObject) => {
    return await getSearchResult(searchObject, Endpoints.CORRESPONDENCE_BUYER_SEARCH(roundId));
}

const getSearchResult = async (searchObject, url) => {
    //const state = reduxStore.getState();
    let params = applySearchParams(searchObject);

    const threadList = await RestRequestGET(url, {params});
    if (threadList.success !== false) {
        cleanupThreadList();
        reduxStore.dispatch(setResource(CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID, threadList.checkTime));
        reduxStore.dispatch(setResource(CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID, threadList.correspondenceSplitDate));
        reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID, threadList.beforeBiddingEndThreads));
        reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID, threadList.afterBiddingEndThreads));
        reduxStore.dispatch(setResource(CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID, threadList.notSplicedThreads));
        reduxStore.dispatch(setResource(CORRESPONDENCE_DOWNLOAD_ATTACHMENT_REGISTRATION_DATE_RESOURCE_ID, threadList.attachmentDownloadRegistrationStartDate))
    }
    return threadList;
}

const getBuyerPrint = async (roundId, type, before, threadIds) => {
    return print(Endpoints.CORRESPONDENCE_BUYER_PRINT(roundId, type, before), threadIds);
}

const getContractorPrint = async (roundId, type, before, threadIds) => {
    return print(Endpoints.CORRESPONDENCE_CONTRACTOR_PRINT(roundId, type, before), threadIds);
}

const getBuyerContractorEmailList = async (roundId, departmentId) => {
    return await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_CONTRACTOR_EMAIL_LIST(roundId, departmentId));
}

const getBuyerThreadEmailList = async (roundId, threadId) => {
    return await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_THREAD_EMAIL_LIST(roundId, threadId));
}

const print = (url, threadIds) => {
    const data = {
        items: threadIds
    }
    return RestRequestBLOB(url, {data}, 'post');
}

const shareCorrespondencePrint = async (roundId, type, before, threadIds, system) => {
    let params = {
        threadIds: threadIds.join()
    }
    try {
        const resp = await RestRequestPOST(Endpoints.CORRESPONDENCE_BUYER_SHARE(roundId, type, before), {system}, {params});
        if (isResponseValid(resp, (e) => reduxStore.dispatch(setSaveStatusError(e)))) {
            return true;
        }
    } catch (e) {
        console.error(e)
        reduxStore.dispatch(setSaveStatusError(Tr('internal_server_error')))
    }
    return false;
}

const loadLastShareHistory = async (roundId, before) => {
    return await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_SHARE_HISTORY_LAST(roundId, before));
}

const loadShareHistory = async (roundId, before, system) => {
    return await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_SHARE_HISTORY(roundId, before, system));
}

const _extractHasUnsentMessagesResult = (resp) => {
    const payload = resp?.result;
    if (resp?.success === false || !payload || typeof payload !== 'object') {
        return {hasUnsentMessages: false, threadIds: []};
    }
    const threadIds = Array.isArray(payload.threadIds) ? payload.threadIds : [];
    return {
        hasUnsentMessages: typeof payload.hasUnsentMessages === 'boolean'
            ? payload.hasUnsentMessages
            : threadIds.length > 0,
        threadIds
    };
};

const getBuyerHasUnsentMessages = async (roundId, before) => {
    const resp = await RestRequestGET(Endpoints.CORRESPONDENCE_BUYER_HAS_UNSENT_MESSAGES(roundId, before));
    return _extractHasUnsentMessagesResult(resp);
}

const getContractorHasUnsentMessages = async (roundId, before) => {
    const resp = await RestRequestGET(Endpoints.CORRESPONDENCE_CONTRACTOR_HAS_UNSENT_MESSAGES(roundId, before));
    return _extractHasUnsentMessagesResult(resp);
}

export default {
    getBuyerThreadList,
    getBuyerNewMessages,
    getBuyerSearchResult,
    getBuyerThreadMessageList,
    addBuyerThread,
    sendBuyerMessage,
    publishBuyerMessage,
    updateBuyerMessage,
    saveBuyerThreadAsContractor,
    createBuyerMessage,
    deleteBuyerMessage,
    sendBuyerBulkMessages,
    publishBuyerBulkMessages,
    getBuyerContractorInRoundList,
    getBuyerMessageDelivery,
    downloadBuyerMessageAttachmentsZip,
    downloadBuyerMessageAttachment,
    checkVerificationLimit,
    printBuyerMessageAttachmentsVerification,
    sendBuyerFlagThread,
    sendBuyerUnflagThread,
    getBuyerPrivileges,
    getBuyerPrint,
    getBuyerThreadEmailList,
    getBuyerContractorEmailList,
    shareCorrespondencePrint,
    loadLastShareHistory,
    loadShareHistory,
    getBuyerHasUnsentMessages,
    getContractorHasUnsentMessages,

    cleanupThreadList,
    cleanupPrivileges,
    clearThreadMessages,

    getContractorThreadList,
    getContractorNewMessages,
    getContractorSearchResult,
    getContractorThreadMessageList,
    createContractorMessage,
    updateContractorMessage,
    sendContractorMessage,
    deleteContractorMessage,
    addContractorThread,
    downloadContractorMessageAttachmentsZip,
    downloadContractorMessageAttachment,
    sendContractorFlagThread,
    sendContractorUnflagThread,
    getContractorPrivileges,
    getContractorPrint,

    CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID,
    CORRESPONDENCE_THREAD_LISTS_AFTER_END_DATE_RESOURCE_ID,
    CORRESPONDENCE_THREAD_LISTS_NOT_SPLICED_RESOURCE_ID,
    CORRESPONDENCE_LAST_CHECK_TIME_RESOURCE_ID,
    CORRESPONDENCE_SPLIT_DATE_RESOURCE_ID,
    CORRESPONDENCE_DOWNLOAD_ATTACHMENT_REGISTRATION_DATE_RESOURCE_ID
}